import { accountsRoutes, documentsRoutes, groupsRoutes, organizationRoutes, reportsRoutes } from '@/backend/config/routes';
import { canViewLogiReports } from '@/backend/features/logi-reports/shared';
import { allowAccessToDocuments, allowAccessToReports } from '@/backend/features/permissions/utils';
import { GlobalAppRouteObject } from '@juvare/common-web-ui/GlobalApp';
import { useFeatureFlag } from '@juvare/common-web-ui/hooks';
import {
    allowAccessToAccounts,
    allowAccessToGroups,
    allowAccessToUserDirectory,
    requireAnySystemPermission,
} from '@juvare/contact-management/backend/features/permissions/shared';
import { AccountPage, AccountsPanel } from '@juvare/contact-management/ui/features/accounts';
import { GroupsPanel } from '@juvare/contact-management/ui/features/groups/components/GroupsPanel';
import { UserDirectoryPanel } from '@juvare/contact-management/ui/features/user-directory';
import { useUserState } from '@juvare/contact-management/ui/hooks';
import { hasEnabledFeature } from '@juvare/contact-management/utils';
import { DocumentPanel } from '@juvare/document-library/ui/features/file-manager';
import { LogiPanel } from '@juvare/logi-reports/ui/features/logi-panel';
import BarChartIcon from '@mui/icons-material/BarChart';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import FileOpenIcon from '@mui/icons-material/FileOpen';
import FileOpenOutlinedIcon from '@mui/icons-material/FileOpenOutlined';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import FolderSharedOutlinedIcon from '@mui/icons-material/FolderSharedOutlined';
import GroupIcon from '@mui/icons-material/Group';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import HomeIcon from '@mui/icons-material/Home';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import LanIcon from '@mui/icons-material/Lan';
import LanOutlinedIcon from '@mui/icons-material/LanOutlined';
import PermContactCalendarIcon from '@mui/icons-material/PermContactCalendar';
import PermContactCalendarOutlinedIcon from '@mui/icons-material/PermContactCalendarOutlined';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { NavigateFunction } from 'react-router';
import { HomePanel } from '../components/HomePanel';
import { ReportsPanel } from '../components/ReportsPanel';
import { HanOrganizationsPanelNew } from '../features/contact-management/components/Organizations/OrganizationsPanel';

export const useCreateGlobalMenuRoutes = () => {
    const { t } = useTranslation('healthAlerts');
    const userState = useUserState();

    const { enabled: isLogiReportsEnabled } = useFeatureFlag('han', 'logiReportsEnabled');

    const createGlobalMenuRoutes = (alertsRoutes: GlobalAppRouteObject[]) => {
        const routes: GlobalAppRouteObject[] = [];

        // Everybody has access to Home page
        routes.push({
            index: true,
            element: <HomePanel />,
            handle: {
                type: 'page',
                label: t('panels.home.title'),
                icon: <HomeOutlinedIcon />,
                iconSelected: <HomeIcon />,
                navigateTo: './',
            },
        });

        // Notification routes come from alert-new-ui
        routes.push(...alertsRoutes);

        // Special permissions required for Accounts page
        if (allowAccessToAccounts(userState)) {
            routes.push({
                path: accountsRoutes.root + '/*',
                element: <AccountsPanel />,
                handle: {
                    type: 'page',
                    onClick: (navigate: NavigateFunction) => {
                        const urlParams = new URLSearchParams();
                        urlParams.set('filter', JSON.stringify({ systemStatus: ['active'] }));
                        navigate({ pathname: 'accounts', search: urlParams.toString() });
                    },
                    label: t('panels.accounts.title'),
                    icon: <PermContactCalendarOutlinedIcon />,
                    iconSelected: <PermContactCalendarIcon />,
                },
            });
        } else {
            // If no permissions for Accounts page, then at least allow the account profile page
            routes.push({
                path: accountsRoutes.root + '/:accountId/*',
                element: <AccountPage />,
            });
        }

        if (hasEnabledFeature(userState, 'userDirectory') && allowAccessToUserDirectory(userState)) {
            routes.push({
                path: 'user-directory/*',
                element: <UserDirectoryPanel />,
                handle: {
                    type: 'page',
                    label: t('panels.userDirectory.title'),
                    icon: <FolderSharedOutlinedIcon />,
                    iconSelected: <FolderSharedIcon />,
                },
            });
        }

        // Special permissions required for Groups page
        if (allowAccessToGroups(userState)) {
            routes.push({
                path: groupsRoutes.root + '/*',
                element: <GroupsPanel />,
                handle: {
                    type: 'page',
                    label: t('panels.groups.title'),
                    icon: <GroupOutlinedIcon />,
                    iconSelected: <GroupIcon />,
                },
            });
        }

        // Special permissions required for Organizations page
        if (requireAnySystemPermission(userState, 'view-organizations')) {
            routes.push({
                path: organizationRoutes.root + '/*',
                element: <HanOrganizationsPanelNew />,
                handle: {
                    type: 'page',
                    label: t('panels.organizations.title'),
                    icon: <LanOutlinedIcon />,
                    iconSelected: <LanIcon />,
                },
            });
        }

        if (allowAccessToDocuments(userState)) {
            routes.push({
                path: documentsRoutes.root,
                element: <DocumentPanel />,
                children: [{ path: '*', element: <DocumentPanel /> }],
                handle: {
                    type: 'page',
                    label: t('panels.documents.title'),
                    icon: <FolderOutlinedIcon />,
                    iconSelected: <FolderIcon />,
                },
            });
        }

        if (isLogiReportsEnabled && userState.isLogiEnabled && canViewLogiReports(userState)) {
            routes.push({
                path: reportsRoutes.root + '/*',
                element: <LogiPanel />,
                handle: {
                    type: 'page',
                    label: t('panels.reports.title'),
                    icon: <BarChartOutlinedIcon />,
                    iconSelected: <BarChartIcon />,
                },
            });
        }

        if (!isLogiReportsEnabled && allowAccessToReports(userState)) {
            routes.push({
                path: reportsRoutes.root,
                element: <ReportsPanel />,
                handle: {
                    type: 'page',
                    label: t('panels.reports.title'),
                    icon: <FileOpenOutlinedIcon />,
                    iconSelected: <FileOpenIcon />,
                },
            });
        }

        return routes;
    };

    return { createGlobalMenuRoutes };
};
